
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ModularConstructionPage = () => {
  const navigate = useNavigate();
  
  // List of 25 Knowledge Bases in the Modular Construction space
  const knowledgeBases = [
    { id: 1, title: "CURT Owners Blueprint to Modularization", author: "Construction Users Roundtable", year: 2022 },
    { id: 2, title: "Off-Site Fabrication: Prefabrication, Preassembly and Modularization", author: "Construction Users Roundtable", year: 2021 },
    { id: 3, title: "Design for Manufacturing and Assembly (DfMA) in Construction", author: "American Institute of Architects", year: 2023 },
    { id: 4, title: "Modular Building Systems Performance Standards", author: "International Code Council", year: 2022 },
    { id: 5, title: "Supply Chain Management for Modular Construction", author: "Modular Building Institute", year: 2021 },
    { id: 6, title: "Quality Assurance Protocols for Modular Construction", author: "Construction Quality Association", year: 2023 },
    { id: 7, title: "Lean Manufacturing Principles Applied to Modular Construction", author: "Lean Construction Institute", year: 2022 },
    { id: 8, title: "Digital Technologies for Modular Construction Projects", author: "Autodesk Research", year: 2023 },
    { id: 9, title: "Cost-Benefit Analysis of Modular vs. Traditional Construction", author: "Construction Users Roundtable", year: 2021 },
    { id: 10, title: "Modular Construction Case Studies: Success and Failure Analysis", author: "Construction Research Board", year: 2022 },
    { id: 11, title: "Transportation Logistics for Modular Units", author: "Modular Transportation Alliance", year: 2021 },
    { id: 12, title: "Structural Engineering for Modular Buildings", author: "American Society of Civil Engineers", year: 2022 },
    { id: 13, title: "Building Information Modeling (BIM) for Modular Construction", author: "BIM Forum", year: 2023 },
    { id: 14, title: "Workforce Training for Modular Construction Skills", author: "Association of Builders and Contractors", year: 2022 },
    { id: 15, title: "Regulatory Compliance for Modular Buildings", author: "International Association of Building Officials", year: 2021 },
    { id: 16, title: "Sustainability and Energy Efficiency in Modular Construction", author: "Green Building Council", year: 2023 },
    { id: 17, title: "Advanced Materials for Modular Construction", author: "Materials Research Society", year: 2022 },
    { id: 18, title: "Modular Construction for Healthcare Facilities", author: "Healthcare Construction Forum", year: 2021 },
    { id: 19, title: "Modular Solutions for Affordable Housing", author: "Housing Research Institute", year: 2023 },
    { id: 20, title: "Acoustic Performance in Modular Buildings", author: "Acoustical Society of America", year: 2022 },
    { id: 21, title: "Fire Safety in Modular Construction", author: "Fire Protection Association", year: 2021 },
    { id: 22, title: "MEP Systems Integration in Modular Buildings", author: "ASHRAE", year: 2023 },
    { id: 23, title: "Contract and Legal Considerations for Modular Projects", author: "Construction Law Society", year: 2022 },
    { id: 24, title: "Risk Management in Modular Construction", author: "Project Management Institute", year: 2021 },
    { id: 25, title: "Future Trends in Modular and Off-Site Construction", author: "Construction Users Roundtable", year: 2023 }
  ];

  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-6 flex items-center">
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-1"
            onClick={() => navigate('/curt-channel')}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to CURT Channel
          </Button>
        </div>
        
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-3xl font-bold mb-2">Modular Construction</CardTitle>
                <CardDescription className="text-lg">
                  Knowledge Bases in the Modular Construction Domain
                </CardDescription>
              </div>
              <img 
                src="/lovable-uploads/c93c088c-e4f2-4a2d-9d19-35a6371846b9.png" 
                alt="Construction Users Roundtable (CURT) logo" 
                className="w-16 h-16 object-contain"
                loading="lazy"
              />
            </div>
          </CardHeader>
          <CardContent>
            <p className="mb-8 text-muted-foreground">
              This collection contains 25 curated Knowledge Bases covering various aspects of modular construction, 
              including best practices, techniques, standards, and case studies. These resources provide valuable insights 
              for construction professionals looking to implement or improve modular construction approaches.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {knowledgeBases.map((kb) => (
                <Card key={kb.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-lg">{kb.title}</h3>
                    <div className="flex justify-between mt-2 text-sm text-muted-foreground">
                      <span>{kb.author}</span>
                      <span>{kb.year}</span>
                    </div>
                    <Button 
                      variant="link" 
                      className="p-0 h-auto mt-2 text-primary hover:text-primary/80 flex items-center gap-1"
                      onClick={() => {
                        const isBlueprint = kb.title === "CURT Owners Blueprint to Modularization";
                        const base = `/curt-solutions?channel=curt&view=grid&q=${encodeURIComponent(kb.title)}`;
                        const url = isBlueprint ? `${base}&id=curt-owners-blueprint` : base;
                        navigate(url);
                      }}
                    >
                      View Knowledge Base
                      <ExternalLink className="h-3 w-3" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ModularConstructionPage;
